import type { FC } from 'react'
import type { PartSvgProps } from './types'

// ── Imports (single binding per module — avoids Rollup dual-binding TDZ) ─────

import BarrelSvg from './BarrelSvg'
import MuzzleDeviceSvg from './MuzzleDeviceSvg'
import HandguardSvg from './HandguardSvg'
import BcgSvg from './BcgSvg'
import ChargingHandleSvg from './ChargingHandleSvg'
import UpperReceiverSvg from './UpperReceiverSvg'
import LowerReceiverSvg from './LowerReceiverSvg'
import OpticSvg from './OpticSvg'
import RedDotSvg from './RedDotSvg'
import LightSvg from './LightSvg'
import LaserSvg from './LaserSvg'
import TriggerGroupSvg from './TriggerGroupSvg'
import PistolGripSvg from './PistolGripSvg'
import StockSvg from './StockSvg'
import BraceSvg from './BraceSvg'
import SlingMountSvg from './SlingMountSvg'
import BipodSvg from './BipodSvg'
import GenericPartSvg from './GenericPartSvg'
import AkHandguardUpperSvg from './AkHandguardUpperSvg'
import AkHandguardLowerSvg from './AkHandguardLowerSvg'
import AkGasTubeSvg from './AkGasTubeSvg'
import AkDustCoverSvg from './AkDustCoverSvg'
import AkSideRailSvg from './AkSideRailSvg'
import AkMagBananaSvg762 from './AkMagBananaSvg762'
import AkMagBananaSvg545 from './AkMagBananaSvg545'
import AkMagDrumSvg from './AkMagDrumSvg'
import AkBayonetLugSvg from './AkBayonetLugSvg'
import AkIronSightsSvg from './AkIronSightsSvg'

export type { PartSvgProps }

// ── Named re-exports (single-binding form — no dual export+import) ────────────

export {
  BarrelSvg,
  MuzzleDeviceSvg,
  HandguardSvg,
  BcgSvg,
  ChargingHandleSvg,
  UpperReceiverSvg,
  LowerReceiverSvg,
  OpticSvg,
  RedDotSvg,
  LightSvg,
  LaserSvg,
  TriggerGroupSvg,
  PistolGripSvg,
  StockSvg,
  BraceSvg,
  SlingMountSvg,
  BipodSvg,
  GenericPartSvg,
  AkHandguardUpperSvg,
  AkHandguardLowerSvg,
  AkGasTubeSvg,
  AkDustCoverSvg,
  AkSideRailSvg,
  AkMagBananaSvg762,
  AkMagBananaSvg545,
  AkMagDrumSvg,
  AkBayonetLugSvg,
  AkIronSightsSvg
}

// ── Dynamic lookup ────────────────────────────────────────────────────────────

const CATEGORY_SVG_MAP: Record<string, FC<PartSvgProps>> = {
  Barrel: BarrelSvg,
  'Muzzle Device': MuzzleDeviceSvg,
  'Handguard / Rail': HandguardSvg,
  'Bolt Carrier Group': BcgSvg,
  'Charging Handle': ChargingHandleSvg,
  'Upper Receiver': UpperReceiverSvg,
  'Lower Receiver': LowerReceiverSvg,
  Optic: OpticSvg,
  'Red Dot': RedDotSvg,
  'Light / Laser': LightSvg,
  Laser: LaserSvg,
  'Trigger Group': TriggerGroupSvg,
  'Pistol Grip': PistolGripSvg,
  'Stock / Brace': StockSvg,
  Stock: StockSvg,
  Brace: BraceSvg,
  'Sling / Mount': SlingMountSvg,
  Bipod: BipodSvg,
  Other: GenericPartSvg,
  // AK Platform
  'Handguard Upper': AkHandguardUpperSvg,
  'Handguard Lower': AkHandguardLowerSvg,
  'Gas Tube / Piston': AkGasTubeSvg,
  'Dust Cover': AkDustCoverSvg,
  'Side Rail Mount': AkSideRailSvg,
  Magazine: AkMagBananaSvg762,
  Magazine762: AkMagBananaSvg762,
  Magazine545: AkMagBananaSvg545,
  MagazineDrum: AkMagDrumSvg,
  'Bayonet Lug': AkBayonetLugSvg
}

/**
 * Returns the SVG component for the given PartCategory string.
 * Falls back to GenericPartSvg for unknown categories.
 */
export function getPartSvg (category: string | null | undefined): FC<PartSvgProps> {
  if (!category) return GenericPartSvg
  return CATEGORY_SVG_MAP[category] ?? GenericPartSvg
}
